/**
 * TPRA Assessment Stages API Request Hook
 * Fetches assessment pipeline stage counts
 */

import { get } from "@/infra/rest";
import { APIEndpoints } from "@/shared/constants/Endpoints";
import { tpraHomeQueryKeys } from "@/shared/constants/QueryKeys";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import type { AssessmentStagesResponse } from "../types";
import { AssessmentStagesResponseSchema } from "../validation/schemas";

/**
 * Fetches assessment stages data from the API
 * @returns Validated assessment stages response
 * @throws Error if API request fails or response validation fails
 */
async function fetchAssessmentStages(): Promise<AssessmentStagesResponse> {
  const response = await get<AssessmentStagesResponse>({
    url: APIEndpoints.TPRAHomeAssessmentStages,
  });

  // Validate response with Zod schema
  const validated = AssessmentStagesResponseSchema.parse(response);

  return validated;
}

/**
 * React Query hook for fetching TPRA assessment stages
 *
 * Provides:
 * - Automatic caching with TanStack Query
 * - Loading and error states
 * - Automatic refetching on mount
 * - Retry logic for failed requests
 *
 * @returns React Query result with assessment stages data
 *
 * @example
 * ```typescript
 * function AssessmentPipeline() {
 *   const { data, isLoading, isError, refetch } = useGetAssessmentStages();
 *
 *   if (isLoading) return <PipelineSkeleton />;
 *   if (isError) return <SectionError onRetry={refetch} />;
 *
 *   return <PipelineContent stages={data.stages} />;
 * }
 * ```
 */
export function useGetAssessmentStages(): UseQueryResult<
  AssessmentStagesResponse,
  Error
> {
  return useQuery({
    queryKey: tpraHomeQueryKeys.stages(),
    queryFn: fetchAssessmentStages,

    // Caching Strategy
    staleTime: 0, // Always refetch for fresh data
    gcTime: 10 * 60 * 1000, // 10 minutes cache retention

    // Retry Strategy (exponential backoff)
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),

    // Refetch Behavior
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });
}
